import React from 'react';
import { Zone } from '../../types';
import {
    SurveillanceDevice, sectionStyle, btnStyle, inputStyle, labelStyle, chipStyle,
    ALL_PROPERTIES, PROPERTY_LABELS,
} from './surveillanceShared';
import { getDeviceTypeLabel } from '../../styles/shared';

// ── ZoneDevicePicker ─────────────────────────────────────────────────────────

export interface ZoneDevicePickerProps {
    zones: Zone[];
    selectedZone: string;
    onZoneChange: (zone: string) => void;
    devices: SurveillanceDevice[];
    selectedDevices: Set<string> | string[];
    onToggleDevice: (id: string) => void;
    onSelectAll: () => void;
    onClear: () => void;
    extraActions?: React.ReactNode;
}

export const ZoneDevicePicker: React.FC<ZoneDevicePickerProps> = ({
    zones, selectedZone, onZoneChange, devices, selectedDevices, onToggleDevice, onSelectAll, onClear, extraActions,
}) => {
    const isSelected = (id: string) => Array.isArray(selectedDevices) ? selectedDevices.includes(id) : selectedDevices.has(id);
    const count = Array.isArray(selectedDevices) ? selectedDevices.length : selectedDevices.size;
    return (
        <div style={sectionStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '10px' }}>
                <label style={{ ...labelStyle, margin: 0, minWidth: '70px' }}>Zon</label>
                <select style={{ ...inputStyle, width: 'auto', minWidth: '200px' }} value={selectedZone}
                    onChange={e => onZoneChange(e.target.value)}>
                    <option value="">Alla zoner</option>
                    {zones.map(z => (
                        <option key={z.id} value={String(z.id)}>{z.name} ({z.devices.length} enheter)</option>
                    ))}
                </select>
                <div style={{ borderLeft: '1px solid #ddd', paddingLeft: '12px', marginLeft: '4px', display: 'flex', gap: '6px' }}>
                    <button type="button" onClick={onSelectAll} style={btnStyle('#007bff')} title="Välj alla enheter som visas">Välj alla</button>
                    <button type="button" onClick={onClear} style={btnStyle('#6c757d')} title="Avmarkera alla enheter">Rensa</button>
                    {extraActions}
                </div>
            </div>
            <label style={{ ...labelStyle, marginBottom: '8px' }}>
                Enheter{count > 0 && <span style={{ color: '#007bff', fontWeight: 'normal' }}> — {count} valda</span>}
            </label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {devices.length === 0 && (
                    <span style={{ color: '#888', fontStyle: 'italic' }}>
                        {selectedZone ? 'Inga enheter i den här zonen' : 'Inga enheter hittades'}
                    </span>
                )}
                {devices.map(d => {
                    const active = isSelected(d.id);
                    const color = d.status === 'online' ? '#28a745' : '#dc3545';
                    return (
                        <span key={d.id} onClick={() => onToggleDevice(d.id)} style={chipStyle(active, color)}>
                            <span style={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: color, display: 'inline-block' }} />
                            {d.name}
                            <span style={{ color: '#999', fontSize: '11px' }}>({getDeviceTypeLabel(d.type)})</span>
                        </span>
                    );
                })}
            </div>
        </div>
    );
};

// ── PropertyPicker ───────────────────────────────────────────────────────────

export interface PropertyPickerProps {
    available: string[];
    selected: Set<string> | string[];
    loading?: boolean;
    propLabel: (p: string) => string;
    propNameMap: Record<string, string>;
    onToggle: (p: string) => void;
    onSelectAll?: () => void;
    onClear?: () => void;
    emptyMessage?: string;
}

export const PropertyPicker: React.FC<PropertyPickerProps> = ({
    available, selected, loading, propLabel, propNameMap, onToggle, onSelectAll, onClear, emptyMessage,
}) => {
    const isSelected = (p: string) => Array.isArray(selected) ? selected.includes(p) : selected.has(p);
    const count = Array.isArray(selected) ? selected.length : selected.size;
    return (
        <div style={{ ...sectionStyle, backgroundColor: '#f8f9fa', padding: '12px 16px', marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <label style={{ ...labelStyle, marginBottom: 0 }}>
                    Mätvärden{count > 0 && <span style={{ color: '#007bff', fontWeight: 'normal' }}> — {count} valda</span>}
                    {loading && <span style={{ color: '#888', fontWeight: 'normal', marginLeft: '8px', fontSize: '12px' }}>hämtar…</span>}
                </label>
                {(onSelectAll || onClear) && (
                    <div style={{ display: 'flex', gap: '6px' }}>
                        {onSelectAll && <button onClick={onSelectAll} style={{ ...btnStyle('#007bff'), fontSize: '11px', padding: '3px 8px' }}>Alla</button>}
                        {onClear && <button onClick={onClear} style={{ ...btnStyle('#6c757d'), fontSize: '11px', padding: '3px 8px' }}>Rensa</button>}
                    </div>
                )}
            </div>
            {emptyMessage && available.length === 0 && !loading && (
                <p style={{ color: '#888', fontStyle: 'italic', margin: '0 0 8px' }}>{emptyMessage}</p>
            )}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {[...available].sort((a, b) => propLabel(a).localeCompare(propLabel(b))).map(p => (
                    <span key={p} onClick={() => onToggle(p)}
                        title={PROPERTY_LABELS[p] ? p : (propNameMap[p] || undefined)}
                        style={chipStyle(isSelected(p), ALL_PROPERTIES.includes(p) ? '#007bff' : '#6610f2')}>
                        {propLabel(p)}
                    </span>
                ))}
            </div>
        </div>
    );
};
